import { ChangeEventHandler, FC, useMemo } from 'react';

import dayjs from 'dayjs';
import { isNonNullish } from 'remeda';
import useSWR from 'swr';

import Chip from '@mui/material/Chip';

import { AddFreeSpinsButton } from './addFreeSpinsButton/AddFreeSpinsButton';
import { FreeSpinsTableActionsColumn } from './freeSpinsTableColumns/FreeSpinsTableActionsColumn';

import css from './FreeSpinsPage.module.css';

import { providerService } from 'v2/api/providerService';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { PageLayout } from 'v2/components/pageLayout/PageLayout';
import { PageTitle } from 'v2/components/pageLayout/pageTitle/PageTitle';
import { SearchTextField } from 'v2/components/searchTextField/SearchTextField';
import { LONG_DASH } from 'v2/constants';
import { useFilter } from 'v2/hooks/useFilter';
import { usePagination } from 'v2/hooks/usePagination';
import { TableColumn } from 'v2/types';
import { FreeSpinModel, FreeSpinsFilter } from 'v2/types/FreeSpinModel';
import { formatNumber } from 'v2/utils';

export const FreeSpinsPage: FC = () => {
  const { page, pageSize, onPageChange } = usePagination({ defaultPageSize: 30 });

  const { filter, debouncedFilter, onChange } = useFilter<FreeSpinsFilter>({ providerName: '', gameName: '' });

  const { data, mutate } = useSWR(['getFreeSpins', page, pageSize, debouncedFilter], providerService.getFreeSpins, {
    keepPreviousData: true,
  });

  const columns: TableColumn<FreeSpinModel>[] = useMemo(
    () => [
      { id: 'id', key: 'id', label: 'Id', ellipsis: true },
      {
        key: 'providerName',
        label: 'Provider',
        render: (row) => <Chip label={row.providerName} color="secondary" size="small" />,
      },
      {
        id: 'gameName',
        key: 'gameName',
        label: 'Game',
        ellipsis: true,
      },
      {
        id: 'countSpins',
        key: 'countSpins',
        label: 'Spins Count',
      },
      {
        key: 'betSpins',
        label: 'Bet',
        render: (row) => formatNumber(row.betSpins),
      },
      {
        key: 'typeSpins',
        label: 'Type',
        render: (row) => (isNonNullish(row.typeSpins) ? <Chip label={row.typeSpins} size="small" /> : LONG_DASH),
      },
      {
        key: 'createdAt',
        label: 'Created At',
        render: (row) => dayjs(row.createdAt).format('DD.MM.YYYY HH:mm'),
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => <FreeSpinsTableActionsColumn freeSpin={row} onRevalidate={mutate} />,
      },
    ],
    [mutate]
  );

  const handleFilterChange =
    (field: keyof FreeSpinsFilter): ChangeEventHandler<HTMLInputElement> =>
    (e) => {
      onChange(field, e.target.value);
    };

  return (
    <PageLayout className={css.freeSpinsPage} overflow="hidden">
      <PageTitle action={<AddFreeSpinsButton onSuccess={mutate} />}>Free Spins Settings</PageTitle>

      <div className="form-group">
        <SearchTextField value={filter.providerName} label="Provider" onChange={handleFilterChange('providerName')} />

        <SearchTextField value={filter.gameName} label="Game" onChange={handleFilterChange('gameName')} />
      </div>

      <DataTable
        columns={columns}
        data={data?.result}
        total={data?.total}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        withPagination
      />
    </PageLayout>
  );
};
